// Phase 23A — PJP change-request approval grid (manager + admin)
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Check, X, ChevronDown, ChevronRight, Download } from "lucide-react";
import { api, API_BASE, getToken, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import PageHeader from "@/components/PageHeader";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";

const BADGE = {
  submitted:        "bg-amber-100 text-amber-800 border-amber-200",
  manager_approved: "bg-sky-100 text-sky-800 border-sky-200",
  admin_approved:   "bg-emerald-100 text-emerald-800 border-emerald-200",
  rejected:         "bg-rose-100 text-rose-800 border-rose-200",
};

export default function PJPRequests() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [viewerStage, setViewerStage] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [rejectFor, setRejectFor] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page_size: 100 };
      if (statusFilter) params.status = statusFilter;
      const r = await api.get("/pjp/requests", { params });
      setRows(r.data.items || []);
      setViewerStage(r.data.viewer_stage);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [statusFilter]);
  useEffect(() => { load(); }, [load]);

  const approve = async (id) => {
    setBusy(true);
    try {
      await api.post(`/pjp/requests/${id}/approve`, {});
      toast.success("Approved");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const doExport = async () => {
    setExporting(true);
    try {
      const qs = new URLSearchParams();
      if (statusFilter) qs.set("status", statusFilter);
      const url = `${API_BASE}/pjp/requests/export${qs.toString() ? "?" + qs.toString() : ""}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j?.error?.message || j.detail?.message || j.detail || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const stamp = new Date().toISOString().replace(/[-:T]/g, "").slice(0, 13);
      const u = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = u;
      a.download = `pjp_change_requests_${stamp}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(u);
      toast.success("Requests exported to Excel");
    } catch (e) { toast.error(String(e.message || e)); }
    finally { setExporting(false); }
  };

  const doReject = async () => {
    if (!reason.trim() || reason.trim().length < 3) { toast.error("Reason ≥ 3 chars"); return; }
    setBusy(true);
    try {
      await api.post(`/pjp/requests/${rejectFor}/reject`, { rejection_reason: reason });
      toast.success("Rejected");
      setRejectFor(null); setReason("");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="p-4 sm:p-6 space-y-4" data-testid="pjp-requests-page">
      <PageHeader
        breadcrumbs={[{ label: "Admin", to: "/" }, { label: "Field ops" }, { label: "PJP requests" }]}
        title="PJP Change Requests"
        subtitle="Approve or reject field-initiated PJP change requests."
        favorite={{ key: "admin.pjp-requests", type: "screen", label: "PJP Change Requests", route: "/admin/pjp-requests" }}
        testid="pjprequests-page-header"
      />

      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">PJP Change Requests</h1>
          <p className="text-xs text-neutral-500 mt-0.5">
            Viewing as <b>{viewerStage || "…"}</b>. Two-level chain: manager → admin.
          </p>
        </div>
        <Button onClick={doExport} disabled={exporting}
                className="tulsi-gradient text-white"
                data-testid="pjp-requests-download">
          {exporting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Download className="h-4 w-4 mr-1" />}
          {exporting ? "Preparing…" : "Download Excel"}
        </Button>
      </div>

      <div className="flex flex-wrap gap-2" data-testid="pjp-req-chips">
        {[
          { v:"", label:"All" },
          { v:"submitted", label:"Pending Manager" },
          { v:"manager_approved", label:"Pending Admin" },
          { v:"admin_approved", label:"Approved" },
          { v:"rejected", label:"Rejected" },
        ].map(o => (
          <button key={o.v} onClick={() => setStatusFilter(o.v)}
            className={"px-3 py-1 rounded-full text-xs font-semibold border " +
              (statusFilter === o.v ? "bg-[#6D2C91] text-white border-[#6D2C91]"
                : "bg-white text-neutral-700 border-neutral-200 hover:border-[#6D2C91]/50")}
            data-testid={`pjp-req-chip-${o.v || "all"}`}>
            {o.label}
          </button>
        ))}
      </div>

      {loading && <div className="py-10 text-center"><Loader2 className="h-5 w-5 animate-spin mx-auto text-neutral-400" /></div>}
      {!loading && rows.length === 0 && (
        <Card className="border-neutral-200 rounded-2xl">
          <CardContent className="py-16 text-center text-neutral-400 text-sm">No change requests.</CardContent>
        </Card>
      )}

      <div className="space-y-2">
        {rows.map(r => (
          <Card key={r.id} className="border-neutral-200 rounded-2xl overflow-hidden"
                data-testid={`pjp-req-${r.id}`}>
            <button className="w-full text-left"
                     onClick={() => setExpanded(e => e === r.id ? null : r.id)}
                     data-testid={`pjp-req-toggle-${r.id}`}>
              <div className="p-3 flex items-center gap-3">
                {expanded === r.id ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-neutral-900">
                    {r.user_name || "—"}
                    <span className="text-neutral-400 font-normal"> · {r.user_role} · {r.user_code || ""}</span>
                  </div>
                  <div className="text-[11px] text-neutral-500">
                    Change {r.requested_date}:
                    {/* PJP hotfix (2026-02): show beat NAME (fallback to code). */}
                    <span className="ml-1 line-through text-neutral-400"
                          data-testid={`pjp-req-current-beat-${r.id}`}>
                      {r.current_beat?.name || r.current_beat?.code || "—"}
                    </span>
                    <span className="mx-1">→</span>
                    <span className="text-[#6D2C91] font-semibold"
                          data-testid={`pjp-req-requested-beat-${r.id}`}>
                      {r.requested_beat?.name || r.requested_beat?.code || "—"}
                    </span>
                  </div>
                </div>
                <Badge className={"border text-[10px] " + (BADGE[r.status] || "")}
                       data-testid={`pjp-req-status-${r.id}`}>
                  {r.status.replace("_", " ")}
                </Badge>
              </div>
            </button>
            {expanded === r.id && (
              <div className="border-t border-neutral-100 p-3 bg-neutral-50 space-y-2"
                   data-testid={`pjp-req-detail-${r.id}`}>
                <div className="text-xs text-neutral-700" data-testid={`pjp-req-reason-${r.id}`}>
                  <b>Reason:</b> {r.reason}
                </div>
                {r.rejection_reason && (
                  <div className="text-xs text-rose-700">
                    <b>Rejection reason:</b> {r.rejection_reason}
                  </div>
                )}
                {/* Workflow 4 (2026-02): FULL request trail — no slice, and
                    resolve actor name from stage_history when the backend
                    enriches it. Fallback to actor_role + user id. */}
                <div className="space-y-1 pt-1 border-t border-neutral-200"
                     data-testid={`pjp-req-trail-${r.id}`}>
                  <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold">
                    Request trail
                  </div>
                  {(r.stage_history || []).map((h, i) => {
                    const actor = h.actor_name
                      ? `${h.actor_name} (${h.actor_role || "?"})`
                      : (h.actor_role || h.actor_user_id || "—");
                    const testTag =
                      h.action === "submit" ? "requested"
                      : h.action?.startsWith("approve:manager") ? "manager"
                      : h.action?.startsWith("approve:admin") ? "admin"
                      : h.action === "reject" ? "reject"
                      : h.action;
                    return (
                      <div key={i} className="text-[11px] text-neutral-700"
                           data-testid={`pjp-req-trail-${testTag}-${r.id}`}>
                        <span className="font-mono text-neutral-400">
                          {new Date(h.ts).toLocaleString([], { dateStyle: "short", timeStyle: "short" })}
                        </span>
                        {" · "}<b>{actor}</b> <span className="text-neutral-500">— {h.action}</span>
                        {h.comment ? <span className="ml-1 italic text-neutral-600">"{h.comment}"</span> : null}
                      </div>
                    );
                  })}
                </div>
                {/* Workflow 3 (2026-02): two-level lock. Show Approve/Reject
                    ONLY when the viewer's stage matches the request's current
                    stage. Managers see them for `submitted`; admins for
                    `submitted` + `manager_approved`. Once acted, buttons hide
                    and a stage-lock note appears. */}
                {(() => {
                  const isManagerViewer = viewerStage === "manager";
                  const isAdminViewer = viewerStage === "any";
                  const canManagerAct = isManagerViewer && r.status === "submitted";
                  const canAdminAct = isAdminViewer && ["submitted", "manager_approved"].includes(r.status);
                  const showActions = canManagerAct || canAdminAct;
                  const managerAlreadyActed = isManagerViewer && r.status !== "submitted";
                  // The request's date already passed while it sat pending —
                  // the server rejects Approve at that point (materializing a
                  // schedule row for a day that's already gone makes no
                  // sense), so hide it here rather than let the click just
                  // fail with a 422. Reject still works, to clear it out.
                  const isPastDate = r.requested_date < new Date().toISOString().slice(0, 10);
                  return (
                    <>
                      {showActions && (
                        <div className="flex gap-2 pt-2 border-t border-neutral-200"
                             data-testid={`pjp-req-actions-${r.id}`}>
                          {!isPastDate && (
                            <Button size="sm" className="bg-emerald-600 text-white hover:bg-emerald-700"
                                    onClick={() => approve(r.id)} disabled={busy}
                                    data-testid={`pjp-req-approve-${r.id}`}>
                              <Check className="h-3.5 w-3.5 mr-1" /> Approve
                            </Button>
                          )}
                          <Button size="sm" variant="outline"
                                  onClick={() => { setRejectFor(r.id); setReason(""); }}
                                  data-testid={`pjp-req-reject-${r.id}`}>
                            <X className="h-3.5 w-3.5 mr-1 text-red-600" /> Reject
                          </Button>
                        </div>
                      )}
                      {showActions && isPastDate && (
                        <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1.5 mt-2">
                          This date has passed — can no longer be approved, only rejected.
                        </div>
                      )}
                      {managerAlreadyActed && (
                        <div className="text-[11px] text-neutral-600 bg-white border border-neutral-200 rounded-lg px-2 py-1.5 mt-2"
                             data-testid={`pjp-req-lock-note-${r.id}`}>
                          {r.status === "manager_approved" && "Approved by you — pending Admin."}
                          {r.status === "admin_approved" && "Approved · finalized by Admin."}
                          {r.status === "rejected" && "Rejected — user may edit and resubmit."}
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            )}
          </Card>
        ))}
      </div>

      <Dialog open={!!rejectFor} onOpenChange={v => !v && setRejectFor(null)}>
        <DialogContent data-testid="pjp-req-reject-dialog">
          <DialogHeader>
            <DialogTitle>Reject change request</DialogTitle>
            <DialogDescription>User will be able to edit + resubmit; chain restarts at manager.</DialogDescription>
          </DialogHeader>
          <Textarea rows={3} value={reason} onChange={e => setReason(e.target.value)}
                    placeholder="Reason (required, min 3 chars)"
                    data-testid="pjp-req-reject-reason" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectFor(null)}>Cancel</Button>
            <Button className="bg-red-600 text-white hover:bg-red-700" onClick={doReject} disabled={busy}
                    data-testid="pjp-req-reject-confirm">Reject</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
